import type { CreateGlobalAlert, StateGlobalAlert } from '#x1-base/types';
import { type InjectionKey, type Ref } from 'vue';

export const CreateGlobalAlertKey: InjectionKey<CreateGlobalAlert> = Symbol.for('CreateGlobalAlert');

const defaults: StateGlobalAlert = {
    text: '',
    show: false,
    showCloseButton: true,
    showReloadButton: false,
    type: 'success',
    timeout: 5000,
    link: null
};

// Fallback for call sites outside the Nuxt app context. On the client there is
// a single app so tryUseNuxtApp() always resolves and this is never used; on
// the server a context-less write should be discarded (i.e. not retained across
// requests).
let orphanState: Ref<StateGlobalAlert> | null = null;

// Alert state is per Nuxt app (per request on the server) — a module-scope ref
// would be shared by every SSR request for the lifetime of the server process.
export const GlobalAlertState = (): Ref<StateGlobalAlert> => {
    if (tryUseNuxtApp()) {
        return useState<StateGlobalAlert>('keyStateGlobalAlert', () => Object.assign({}, defaults));
    }

    // Client: keep a stable singleton so multiple call sites share the same ref.
    if (import.meta.client) {
        orphanState ??= ref<StateGlobalAlert>(Object.assign({}, defaults));
        return orphanState;
    }

    // Server: return a fresh ref so state can't leak across requests.
    return ref<StateGlobalAlert>(Object.assign({}, defaults));
};

export const createGlobalAlert: CreateGlobalAlert = (options: Partial<StateGlobalAlert> = {}): StateGlobalAlert => {
    const obj = Object.assign({}, defaults, options);
    GlobalAlertState().value = obj;
    return obj;
};

export function useGlobalAlert() {
    const createGlobalAlert = inject(CreateGlobalAlertKey);

    if (!createGlobalAlert) {
        throw new Error('Could not resolve Global Alert provider');
    }

    return createGlobalAlert;
}
